import React from 'react';
import { useUploadPhotoFromUrlMutation } from '@graphql/generated';
import { useFilestack } from '@shared/utils/filestack';
import { useState } from 'react';
import { useToast } from '@withjoy/joykit';
import { WarningTriangle } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { addError } from '@shared/utils/logger';

export function useImageFilePicker({ eventId, initialImageUrl }: { eventId: string; initialImageUrl?: string }) {
  const [updatedImage, setUpdatedImage] = useState<string | undefined>(initialImageUrl);
  const [imageName, setImageName] = useState<string>('');
  const { toast } = useToast();
  const [uploadPhotoFromUrl, { loading: uploadImageFromUrlLoading }] = useUploadPhotoFromUrlMutation();
  const { t } = useTranslation('imageFilePicker');
  const toastPhotoUploadFailed = t('toast', 'toastPhotoUploadFailed');
  const clearImage = () => {
    setUpdatedImage(undefined);
    setImageName('');
  };

  const uploadImage = async (): Promise<{ id: string; url: string } | undefined> => {
    try {
      if (!updatedImage || !imageName) return;

      const { data } = await uploadPhotoFromUrl({
        variables: {
          payload: {
            assetId: imageName,
            containerId: eventId,
            url: updatedImage,
          },
        },
      });
      if (data?.uploadPhotoFromUrl?.id && data?.uploadPhotoFromUrl?.url) {
        return data.uploadPhotoFromUrl;
      } else {
        throw new Error(toastPhotoUploadFailed());
      }
    } catch (e) {
      addError((e as Error).message, { eventId, context: 'usePhotoFilePicker', url: updatedImage, assetId: imageName });
      toast(`${(e as Error).message}`, { icon: <WarningTriangle /> });
      return;
    }
  };

  const { open: openFilepicker, getPhotoPreviewUrl } = useFilestack({
    containerId: eventId,
    onUploadDone: async res => {
      const { filename, url } = res.filesUploaded[0];
      const image = await getPhotoPreviewUrl({
        url,
      });
      setUpdatedImage(image);
      setImageName(filename);
    },
    maxFiles: 1,
  });

  return {
    openFilepicker,
    clearImage,
    updatedImage,
    uploadImage,
    uploadImageFromUrlLoading,
    imageName,
  };
}
